import type { Locale } from '../i18n';

export type ServicePage = {
  slug: string;
  relatedProjects: string[];
  relatedPosts: string[];
  locales: Record<
    Locale,
    {
      title: string;
      h1: string;
      excerpt: string;
      body: string[];
      faqs: { q: string; a: string }[];
      cta: string;
    }
  >;
};

export const SERVICES: ServicePage[] = [
  {
    slug: 'application-mobile-algerie',
    relatedProjects: ['ekrili', 'prof-bilal', 'the-doctor-bita'],
    relatedPosts: ['react-native-algeria-startups', 'offline-first-mobile-apps', 'bilingual-product-ux-algeria'],
    locales: {
      fr: {
        title: 'Application mobile en Algérie | iOS & Android — Codex',
        h1: 'Développement d’applications mobiles en Algérie',
        excerpt:
          'Apps iOS et Android (React Native) pensées pour la 4G algérienne, le bilingue AR/FR et les stores — depuis notre studio à Alger.',
        body: [
          'Nous développons des applications mobiles pour les startups et entreprises en Algérie : marketplaces, santé, éducation, mobilité et apps métier.',
          'React Native nous permet de livrer iOS et Android avec une base commune, tout en respectant les performances locales (réseau intermittent, appareils variés).',
          'Nous gérons le design UI/UX bilingue, les paiements quand disponibles, la géolocalisation, les notifications, et la publication sur App Store et Google Play.',
          'Références locales : Ekrili, Prof Bilal, THE-DOCTOR, Commix — des produits réels avec de vrais utilisateurs.',
        ],
        faqs: [
          {
            q: 'React Native ou natif ?',
            a: 'Pour la plupart des produits en Algérie, React Native offre le meilleur rapport vitesse/coût. Nous recommandons le natif seulement si le cas l’exige.',
          },
          {
            q: 'Aidez-vous pour les stores ?',
            a: 'Oui — comptes développeur, builds, fiches store AR/FR/EN, et mises à jour.',
          },
        ],
        cta: 'Lancer votre app',
      },
      en: {
        title: 'Mobile App Development in Algeria | iOS & Android — Codex',
        h1: 'Mobile app development in Algeria',
        excerpt:
          'iOS and Android apps (React Native) built for Algerian 4G, AR/FR bilingual UX and the stores — from our Algiers studio.',
        body: [
          'We build mobile apps for Algerian startups and companies: marketplaces, health, education, mobility and ops tools.',
          'React Native ships iOS and Android from one codebase while respecting local performance realities.',
          'We handle bilingual UI/UX, payments where available, geo, push, and store publishing.',
          'Local references include Ekrili, Prof Bilal, THE-DOCTOR and Commix.',
        ],
        faqs: [
          {
            q: 'React Native or native?',
            a: 'For most Algeria products, React Native is the best speed/cost trade-off. Native only when the case demands it.',
          },
          {
            q: 'Do you help with the stores?',
            a: 'Yes — developer accounts, builds, store listings, and updates.',
          },
        ],
        cta: 'Launch your app',
      },
      ar: {
        title: 'تطوير تطبيقات الموبايل في الجزائر | iOS وAndroid — كودكس',
        h1: 'تطوير تطبيقات الموبايل في الجزائر',
        excerpt:
          'تطبيقات iOS وAndroid (React Native) مصممة لشبكات الجزائر والواجهة ثنائية اللغة ومتاجر التطبيقات — من استوديونا في العاصمة.',
        body: [
          'نطوّر تطبيقات للشركات الناشئة والمؤسسات في الجزائر: أسواق، صحة، تعليم، تنقّل وأدوات تشغيل.',
          'React Native يتيح iOS وAndroid من قاعدة مشتركة مع مراعاة أداء الشبكة المحلية.',
          'نتولى تجربة المستخدم ثنائية اللغة والدفع والموقع والإشعارات والنشر على المتاجر.',
          'من مراجعنا المحلية: Ekrili وProf Bilal وTHE-DOCTOR وCommix.',
        ],
        faqs: [
          {
            q: 'React Native أم أصلي؟',
            a: 'لأغلب المنتجات في الجزائر، React Native أفضل توازن بين السرعة والتكلفة.',
          },
          {
            q: 'هل تساعدون في المتاجر؟',
            a: 'نعم — حسابات المطوّر والبناء وصفحات المتجر والتحديثات.',
          },
        ],
        cta: 'أطلق تطبيقك',
      },
    },
  },
  {
    slug: 'creation-site-web-alger',
    relatedProjects: ['enginsdz', 'commix', 'az-fulfillment'],
    relatedPosts: ['bilingual-product-ux-algeria', 'choose-software-partner-algeria', 'ci-cd-algiers-teams'],
    locales: {
      fr: {
        title: 'Création de site web à Alger | Agence web — Codex',
        h1: 'Création de site web à Alger',
        excerpt:
          'Sites vitrines et applications web rapides, SEO-ready, en français, arabe et anglais — agence web à Alger.',
        body: [
          'Un site web en Algérie doit charger vite, parler la langue de vos clients, et être trouvé sur Google. Nous concevons des sites et web apps sur mesure depuis Alger.',
          'SEO technique dès la conception : balises, structure, performances, pages services, et contenu FR/AR. Pas de thème générique abandonné après la livraison.',
          'Nous livrons aussi des dashboards et espaces clients lorsque le “site vitrine” doit devenir un produit.',
          'Idéal pour PME, cabinets, industriels et marques qui veulent une présence premium à Alger et en ligne.',
        ],
        faqs: [
          {
            q: 'Faites-vous aussi le SEO ?',
            a: 'Oui — SEO technique inclus dans nos sites ; le contenu et le local SEO (Google Business) se planifient ensemble.',
          },
          {
            q: 'Site WordPress ou sur mesure ?',
            a: 'Selon le besoin. Pour le marketing simple, un CMS peut suffire ; pour un produit, nous préférons une stack moderne maintenable.',
          },
        ],
        cta: 'Créer mon site',
      },
      en: {
        title: 'Web Design & Development in Algiers | Codex',
        h1: 'Web design and development in Algiers',
        excerpt:
          'Fast brochure sites and web apps, SEO-ready, in French, Arabic and English — web agency in Algiers.',
        body: [
          'A website in Algeria must load fast, speak your customers’ language, and rank on Google. We design custom sites and web apps from Algiers.',
          'Technical SEO from day one: tags, structure, performance, service pages, FR/AR content.',
          'We also ship dashboards and client portals when a brochure site must become a product.',
          'Built for SMEs, clinics, industry and brands that want a premium Algiers presence.',
        ],
        faqs: [
          {
            q: 'Do you include SEO?',
            a: 'Technical SEO is included; content and Google Business local SEO are planned with you.',
          },
          {
            q: 'WordPress or custom?',
            a: 'Depends on the job. Simple marketing can use a CMS; products get a modern maintainable stack.',
          },
        ],
        cta: 'Build my website',
      },
      ar: {
        title: 'إنشاء مواقع ويب في الجزائر العاصمة | كودكس',
        h1: 'إنشاء مواقع ويب في الجزائر العاصمة',
        excerpt:
          'مواقع سريعة وتطبيقات ويب جاهزة لـ SEO بالفرنسية والعربية والإنجليزية — وكالة ويب في العاصمة.',
        body: [
          'الموقع في الجزائر يجب أن يحمّل بسرعة ويتحدث لغة زبائنك ويظهر على Google. نصمّم مواقع وتطبيقات من العاصمة.',
          'SEO تقني منذ البداية: وسوم وبنية وأداء وصفحات خدمات ومحتوى عربي/فرنسي.',
          'نسلّم أيضًا لوحات ولوحات عملاء عندما يتحول الموقع إلى منتج.',
          'مناسب للشركات والعيادات والصناعة والعلامات التي تريد حضورًا قويًا في العاصمة وعلى الإنترنت.',
        ],
        faqs: [
          {
            q: 'هل يشمل العرض SEO؟',
            a: 'الـ SEO التقني مضمّن؛ المحتوى وGoogle Business يُخطَّطان معكم.',
          },
          {
            q: 'ووردبريس أم مخصص؟',
            a: 'حسب الحاجة. للتسويق البسيط قد يكفي CMS؛ للمنتجات نفضّل حزمة حديثة قابلة للصيانة.',
          },
        ],
        cta: 'أنشئ موقعي',
      },
    },
  },
  {
    slug: 'developpement-logiciel-algerie',
    relatedProjects: ['skoda-factory', 'az-fulfillment', 'ondefoc'],
    relatedPosts: ['choose-software-partner-algeria', 'build-vs-buy-algeria', 'idea-to-mvp-algiers'],
    locales: {
      fr: {
        title: 'Développement SaaS & logiciel en Algérie | Codex Alger',
        h1: 'Développement SaaS et logiciel sur mesure en Algérie',
        excerpt:
          'Studio IT à Alger depuis 2014 — logiciels métier, plateformes et APIs conçus pour le contexte algérien et les marchés internationaux.',
        body: [
          'Codex conçoit et développe des logiciels sur mesure pour les entreprises algériennes qui ont besoin d’outils fiables, pas de démos jetables. Basés à Alger (Djasr Kasentina), nous livrons des produits web et mobiles utilisés au quotidien.',
          'Notre approche commence par la découverte : processus métier, contraintes réseau, paiements locaux, bilingue AR/FR, et exigences de sécurité. Ensuite seulement vient l’architecture et le code.',
          'Nous construisons des ERP légers, des portails partenaires, des back-offices et des plateformes SaaS — avec React, Node, et des stacks éprouvées. La maintenance et le support font partie du contrat, pas une option.',
          'Que vous soyez une PME à Alger, une usine, une clinique ou une startup, nous cadrons un MVP clair puis itérons. Parlez-nous de votre besoin : nous répondons avec une prochaine étape concrète.',
        ],
        faqs: [
          {
            q: 'Combien coûte un logiciel sur mesure en Algérie ?',
            a: 'Cela dépend du périmètre. Nous proposons un cadrage court avant devis fixe ou forfait par phase — sans surprise sur le scope.',
          },
          {
            q: 'Travaillez-vous hors d’Alger ?',
            a: 'Oui. Notre studio est à Alger, mais nous livrons pour toute l’Algérie et à l’international (Afrique, Europe, Moyen-Orient).',
          },
        ],
        cta: 'Discuter de votre logiciel',
      },
      en: {
        title: 'SaaS & Custom Software Development in Algeria | Codex Algiers',
        h1: 'SaaS and custom software development in Algeria',
        excerpt:
          'Algiers IT studio since 2014 — business software, platforms and APIs built for Algerian reality and international markets.',
        body: [
          'Codex designs and ships custom software for Algerian companies that need reliable tools, not throwaway demos. Based in Algiers, we deliver web and mobile products used every day.',
          'Discovery comes first: workflows, network constraints, local payments, AR/FR bilingual needs, and security. Architecture and code follow.',
          'We build lean ERPs, partner portals, back-offices and SaaS platforms with proven stacks — and we stay for maintenance.',
          'Whether you are an SME, factory, clinic or startup, we scope a clear MVP then iterate. Tell us what you need; we reply with a concrete next step.',
        ],
        faqs: [
          {
            q: 'How much does custom software cost in Algeria?',
            a: 'It depends on scope. We run a short discovery before a fixed quote or phased engagement.',
          },
          {
            q: 'Do you work outside Algiers?',
            a: 'Yes. Our studio is in Algiers; we deliver across Algeria and internationally.',
          },
        ],
        cta: 'Discuss your software',
      },
      ar: {
        title: 'تطوير SaaS وبرمجيات مخصصة في الجزائر | كودكس الجزائر العاصمة',
        h1: 'تطوير SaaS وبرمجيات مخصصة في الجزائر',
        excerpt:
          'استوديو تقني في الجزائر العاصمة منذ 2014 — برمجيات أعمال ومنصات وواجهات API تناسب الواقع الجزائري والأسواق الدولية.',
        body: [
          'تصمّم Codex وتبني برمجيات مخصصة للشركات الجزائرية التي تحتاج أدوات موثوقة لا عروضًا تجريبية. مقرّنا في الجزائر العاصمة ونسلّم منتجات ويب وموبايل تُستخدم يوميًا.',
          'نبدأ بالاستكشاف: سير العمل، قيود الشبكة، المدفوعات المحلية، الثنائية العربية/الفرنسية، والأمن — ثم الهندسة والبرمجة.',
          'نبني أنظمة إدارة خفيفة وبوابات شركاء ولوحات إدارة ومنصات SaaS مع صيانة مستمرة.',
          'سواء كنت مؤسسة صغيرة أو مصنعًا أو عيادة أو شركة ناشئة، نحدد MVP واضحًا ثم نكرّر. أخبرنا بحاجتك وسنرد بخطوة تالية ملموسة.',
        ],
        faqs: [
          {
            q: 'كم يكلف تطوير برمجيات مخصصة في الجزائر؟',
            a: 'يعتمد على النطاق. نبدأ باكتشاف قصير قبل عرض سعر ثابت أو مراحل.',
          },
          {
            q: 'هل تعملون خارج العاصمة؟',
            a: 'نعم. الاستوديو في الجزائر العاصمة ونخدم كل الجزائر والدولي.',
          },
        ],
        cta: 'ناقش برمجياتك',
      },
    },
  },
  {
    slug: 'cloud-devops-algerie',
    relatedProjects: ['skoda-factory', 'commix'],
    relatedPosts: ['cloud-migration-north-africa', 'ci-cd-algiers-teams', 'api-integrations-algeria'],
    locales: {
      fr: {
        title: 'Cloud & DevOps en Algérie | Codex Alger',
        h1: 'Cloud et DevOps pour entreprises en Algérie',
        excerpt:
          'Migration cloud, CI/CD, Docker/Kubernetes et observabilité — adaptés aux contraintes d’infra et de conformité en Algérie.',
        body: [
          'Nous aidons les équipes algériennes à sortir du “serveur unique” pour des déploiements reproductibles : CI/CD, conteneurs, environnements staging/prod, et monitoring.',
          'AWS, Azure ou hébergement régional selon vos contraintes. L’important : backups, secrets, accès, et un pipeline que votre équipe peut opérer.',
          'Nous formons vos ingénieurs et documentons les runbooks — pas de boîte noire.',
          'Idéal si vous scalez une app mobile, un SaaS, ou une plateforme métier critique.',
        ],
        faqs: [
          {
            q: 'Cloud public ou serveurs locaux ?',
            a: 'Nous recommandons selon latence, conformité et budget. Souvent un hybride pragmatique.',
          },
        ],
        cta: 'Parler cloud',
      },
      en: {
        title: 'Cloud & DevOps in Algeria | Codex Algiers',
        h1: 'Cloud and DevOps for Algerian teams',
        excerpt:
          'Cloud migration, CI/CD, Docker/Kubernetes and observability — fit for Algeria infrastructure realities.',
        body: [
          'We help Algerian teams leave the single-server pattern for reproducible deploys: CI/CD, containers, staging/prod, monitoring.',
          'AWS, Azure or regional hosting depending on constraints — with backups, secrets and operable pipelines.',
          'We train your engineers and leave runbooks, not black boxes.',
        ],
        faqs: [
          {
            q: 'Public cloud or local servers?',
            a: 'We recommend based on latency, compliance and budget — often a pragmatic hybrid.',
          },
        ],
        cta: 'Talk cloud',
      },
      ar: {
        title: 'السحابة وDevOps في الجزائر | كودكس',
        h1: 'السحابة وDevOps لفرق الجزائر',
        excerpt:
          'ترحيل سحابي وCI/CD وDocker/Kubernetes ومراقبة — بما يناسب واقع البنية في الجزائر.',
        body: [
          'نساعد الفرق على الانتقال من خادم واحد إلى نشر قابل للتكرار: CI/CD وحاويات وبيئات وتجربة ومراقبة.',
          'AWS أو Azure أو استضافة إقليمية حسب القيود — مع نسخ احتياطي وأسرار وأنابيب تشغيل واضحة.',
          'ندرّب مهندسيكم ونترك أدلة تشغيل لا صناديق سوداء.',
        ],
        faqs: [
          {
            q: 'سحابة عامة أم خوادم محلية؟',
            a: 'نوصي حسب الكمون والامتثال والميزانية — غالبًا هجين عملي.',
          },
        ],
        cta: 'نتحدث عن السحابة',
      },
    },
  },
  {
    slug: 'cybersecurite-algerie',
    relatedProjects: ['skoda-factory', 'the-doctor-bita'],
    relatedPosts: ['cybersecurity-sme-algeria', 'cloud-migration-north-africa', 'api-integrations-algeria'],
    locales: {
      fr: {
        title: 'Cybersécurité en Algérie | PME & entreprises — Codex',
        h1: 'Cybersécurité pour PME et entreprises en Algérie',
        excerpt:
          'Audits, durcissement, sauvegardes et bonnes pratiques identité — pour les organisations algériennes sans budget Fortune 500.',
        body: [
          'La cybersécurité en Algérie commence par l’hygiène : comptes uniques, MFA, sauvegardes testées, mises à jour, et moindre privilège.',
          'Nous auditons vos apps et infra, corrigeons les failles prioritaires, et mettons en place un monitoring pragmatique.',
          'Pour la santé, la finance et l’industrie, nous renforçons aussi la gestion des accès et la journalisation.',
          'Objectif : réduire le risque réel, pas produire un rapport oublié.',
        ],
        faqs: [
          {
            q: 'Proposez-vous des pentests ?',
            a: 'Oui, des tests ciblés sur vos apps web/mobile et un plan de remédiation priorisé.',
          },
        ],
        cta: 'Auditer mon SI',
      },
      en: {
        title: 'Cybersecurity in Algeria | SMEs & enterprise — Codex',
        h1: 'Cybersecurity for Algerian SMEs and enterprises',
        excerpt:
          'Audits, hardening, backups and identity hygiene — for Algerian organisations without Fortune-500 budgets.',
        body: [
          'Security in Algeria starts with hygiene: unique accounts, MFA, tested backups, updates, least privilege.',
          'We audit apps and infra, fix priority issues, and set pragmatic monitoring.',
          'For health, finance and industry we also tighten access and logging.',
        ],
        faqs: [
          {
            q: 'Do you offer pentests?',
            a: 'Yes — focused web/mobile tests with a prioritised remediation plan.',
          },
        ],
        cta: 'Audit my systems',
      },
      ar: {
        title: 'الأمن السيبراني في الجزائر | للمؤسسات — كودكس',
        h1: 'أمن سيبراني للمؤسسات والشركات في الجزائر',
        excerpt:
          'تدقيق وتقوية ونسخ احتياطي ونظافة هوية — لمنظمات جزائرية بميزانيات واقعية.',
        body: [
          'يبدأ الأمن في الجزائر بالنظافة: حسابات فريدة وMFA ونسخ مختبرة وتحديثات وصلاحيات أدنى.',
          'ندقّق التطبيقات والبنية ونصلح الأولويات ونضع مراقبة عملية.',
          'للصحة والمالية والصناعة نشدّد أيضًا الوصول والسجلات.',
        ],
        faqs: [
          {
            q: 'هل تقدّمون اختبار اختراق؟',
            a: 'نعم — اختبارات مركّزة للويب/الموبايل مع خطة إصلاح مرتّبة.',
          },
        ],
        cta: 'دقّق أنظمتي',
      },
    },
  },
  {
    slug: 'agence-digitale-alger',
    relatedProjects: ['ekrili', 'cnhj', 'ondefoc'],
    relatedPosts: ['choose-software-partner-algeria', 'idea-to-mvp-algiers', 'bilingual-product-ux-algeria'],
    locales: {
      fr: {
        title: 'Agence digitale à Alger | Codex — partenaire tech',
        h1: 'Agence digitale à Alger',
        excerpt:
          'Agence digitale et studio logiciel à Alger : produit, design, web, mobile et growth — un seul partenaire de confiance.',
        body: [
          'Codex est votre agence digitale à Alger pour transformer une idée en produit digital. Design, ingénierie et mise en production sous un même toit.',
          'Nous ne livrons pas seulement des maquettes : nous shippons des applications utilisées par de vrais clients en Algérie et à l’étranger.',
          'Services : sites et apps, logiciels métier, cloud, data, cybersécurité, et accompagnement produit.',
          'Depuis 2014, SARL Groupe Codex accompagne startups et entreprises depuis Djasr Kasentina, Alger.',
        ],
        faqs: [
          {
            q: 'Agence ou studio logiciel ?',
            a: 'Les deux : nous avons la culture produit d’un studio et la capacité delivery d’une agence IT.',
          },
        ],
        cta: 'Travailler avec Codex',
      },
      en: {
        title: 'Digital Agency in Algiers | Codex — tech partner',
        h1: 'Digital agency in Algiers',
        excerpt:
          'Digital agency and software studio in Algiers: product, design, web, mobile and growth — one trusted partner.',
        body: [
          'Codex is your Algiers digital agency to turn ideas into shipped products — design, engineering and go-live under one roof.',
          'We ship applications used by real customers in Algeria and abroad.',
          'Since 2014, SARL Groupe Codex has partnered with startups and companies from Djasr Kasentina, Algiers.',
        ],
        faqs: [
          {
            q: 'Agency or software studio?',
            a: 'Both — product culture of a studio with IT agency delivery capacity.',
          },
        ],
        cta: 'Work with Codex',
      },
      ar: {
        title: 'وكالة رقمية في الجزائر العاصمة | كودكس',
        h1: 'وكالة رقمية في الجزائر العاصمة',
        excerpt:
          'وكالة رقمية واستوديو برمجيات في العاصمة: منتج وتصميم وويب وموبايل ونمو — شريك موثوق واحد.',
        body: [
          'كودكس وكالتك الرقمية في العاصمة لتحويل الفكرة إلى منتج يُسلَّم — تصميم وهندسة وإطلاق تحت سقف واحد.',
          'نُطلق تطبيقات يستخدمها عملاء حقيقيون في الجزائر والخارج.',
          'منذ 2014 ترافق SARL Groupe Codex الشركات من جسر قسنطينة، الجزائر العاصمة.',
        ],
        faqs: [
          {
            q: 'وكالة أم استوديو؟',
            a: 'الاثنان — ثقافة منتج الاستوديو وقدرة تسليم وكالة تقنية.',
          },
        ],
        cta: 'اعمل مع كودكس',
      },
    },
  }
];

export function getService(slug: string) {
  return SERVICES.find((s) => s.slug === slug);
}
